import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Box,
    TextField,
    Button,
    Snackbar,
    Alert,
    Typography,
    Grid,
} from '@mui/material';
import Loader from '../../../components/Loader';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import Modal from '../../../components/general/modal';
import { getServicesInvestigationService, updateServiceInvestigationService } from '../../../services/agenda';
import { deleteServiceService } from '../../../services';
import { IServiceInvestigation } from '../../hospital/Request/types';
import { useSnackBarState } from '../../../hooks';
import { Translate } from 'react-localize-redux';

interface ServiceInvestigationConfigurationProps {
    uuidInvestigation: string;
}

const SERVICE_TYPE_LABELS: Record<number, string> = {
    0: 'Lab',
    1: 'Image',
    2: 'Consultation',
    3: 'Shoe',
    4: 'Surgery',
    5: 'Inpatients',
    6: 'Tariff',
    7: 'Emergency',
    8: 'Pharmacy',
};

const ServiceInvestigationConfiguration: React.FC<ServiceInvestigationConfigurationProps> = ({ uuidInvestigation }) => {
    const [loading, setLoading] = useState(true);
    const [services, setServices] = useState<IServiceInvestigation[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingService, setEditingService] = useState<IServiceInvestigation | null>(null);
    const [deletingService, setDeletingService] = useState<IServiceInvestigation | null>(null);
    const [editDescription, setEditDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();
    const [showModal, setShowModal] = useState(false);

    const fetchAllServices = useCallback(async () => {
        try {
            setLoading(true);
            const typeValues = [0, 1, 2, 3, 4, 5, 6, 7, 8];
            const results = await Promise.all(
                typeValues.map(type => getServicesInvestigationService(uuidInvestigation, type)
                    .then(r => ({ ...r, fetchedType: type }))
                    .catch(() => ({ status: 0, servicesInvestigation: [] as IServiceInvestigation[], fetchedType: type })))
            );
            const merged = results.flatMap(r => r.status === 200
                ? r.servicesInvestigation.map(s => ({ ...s, type: s.type ?? r.fetchedType }))
                : []);
            setServices(merged);
        } catch (error) {
            setShowSnackbar({ show: true, message: 'hospital.billing.services.error.load', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [uuidInvestigation]);

    useEffect(() => {
        if (uuidInvestigation) {
            fetchAllServices();
        }
    }, [uuidInvestigation, fetchAllServices]);

    const filteredServices = useMemo(() => {
        if (!searchTerm.trim()) return services;
        const term = searchTerm.toLowerCase();
        return services.filter(s =>
            s.description?.toLowerCase().includes(term) ||
            s.service?.name?.toLowerCase().includes(term)
        );
    }, [services, searchTerm]);

    const headCells = [
        { id: 'description', alignment: 'left', label: <Translate id="hospital.billing.services.description" /> },
        { id: 'serviceName', alignment: 'left', label: <Translate id="hospital.billing.services.service_name" /> },
        { id: 'category', alignment: 'left', label: <Translate id="hospital.billing.services.category" /> },
        { id: 'type', alignment: 'left', label: <Translate id="hospital.billing.services.type" /> },
    ];

    const rows = useMemo(() => {
        return filteredServices.map(service => ({
            id: service.id,
            description: service.description || '',
            serviceName: service.service?.name || '',
            category: service.service?.category || '',
            type: SERVICE_TYPE_LABELS[service.type] ?? service.type,
        }));
    }, [filteredServices]);

    const handleEdit = (id: number) => {
        const service = services.find(s => s.id === id);
        if (service) {
            setEditingService(service);
            setEditDescription(service.description || '');
            setShowModal(true);
        }
    };

    const handleDeleteConfirm = (id: number) => {
        const service = services.find(s => s.id === id);
        if (service) {
            setDeletingService(service);
            setShowModal(true);
        }
    };

    const resetModal = () => {
        setShowModal(false);
        setEditingService(null);
        setDeletingService(null);
        setEditDescription('');
    };

    const handleSave = async () => {
        if (!editingService) return;
        try {
            setSaving(true);
            const payload: IServiceInvestigation = {
                ...editingService,
                description: editDescription,
            };
            const response = await updateServiceInvestigationService(uuidInvestigation, payload);
            if (response.status === 200) {
                setServices(prev => prev.map(s => s.id === editingService.id ? { ...s, description: editDescription } : s));
                setShowSnackbar({ show: true, message: 'hospital.billing.services.success.updated', severity: 'success' });
                resetModal();
            }
        } catch (error: any) {
            const errorCode = error?.response?.data?.errorCode;
            const message = typeof errorCode === 'string'
                ? `hospital.billing.services.errors.error_${errorCode}`
                : 'hospital.billing.services.error.update';
            setShowSnackbar({ show: true, message, severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deletingService) return;
        try {
            setSaving(true);
            await deleteServiceService(uuidInvestigation, deletingService.id);
            setServices(prev => prev.filter(s => s.id !== deletingService.id));
            setShowSnackbar({ show: true, message: 'hospital.billing.services.success.deleted', severity: 'success' });
            resetModal();
        } catch (error: any) {
            const errorCode = error?.response?.data?.errorCode;
            const message = typeof errorCode === 'string'
                ? `hospital.billing.services.errors.error_${errorCode}`
                : 'hospital.billing.services.error.delete';
            setShowSnackbar({ show: true, message, severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const actions = [
        { type: 'edit', func: (id: number) => handleEdit(id) },
        { type: 'delete', func: (id: number) => handleDeleteConfirm(id) },
    ];

    const renderModalContent = () => {
        if (editingService) {
            return (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            {editingService.service?.name}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            autoFocus
                            fullWidth
                            label={<Translate id="hospital.billing.services.description" />}
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            variant="outlined"
                            disabled={saving}
                        />
                    </Grid>
                </Grid>
            );
        }
        if (deletingService) {
            return (
                <Typography variant="body1">
                    <Translate id="hospital.billing.services.confirm_delete" /> <strong>{deletingService.description || deletingService.service?.name}</strong>?
                </Typography>
            );
        }
        return null;
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Box sx={{ p: 2 }}>
            <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder=""
                label={<Translate id="hospital.billing.services.search" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
            />

            <EnhancedTable
                noHeader
                noSelectable
                headCells={headCells}
                rows={rows}
                actions={actions}
            />

            <Modal
                open={showModal}
                title={editingService
                    ? <Translate id="hospital.billing.services.edit_title" />
                    : <Translate id="hospital.billing.services.delete_title" />
                }
                closeModal={resetModal}
                confirmAction={editingService ? handleSave : handleDelete}
            >
                {renderModalContent()}
            </Modal>

            <Snackbar
                open={showSnackbar.show}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={showSnackbar.severity || 'info'} sx={{ width: '100%' }}>
                    {showSnackbar.message && (
                        typeof showSnackbar.message === 'string' && showSnackbar.message.startsWith('hospital.')
                            ? <Translate id={showSnackbar.message} />
                            : showSnackbar.message
                    )}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ServiceInvestigationConfiguration;
